/**
 * The item editor: two columns, no tabs, one scroll.
 *
 * The split is the point. The left column is what the dish *is* — its name, its
 * price, its choices, when it is served, whether it is on offer. The right is
 * how it is *sold* — whether it is on, which category it sits in, what it looks
 * like. A merchant changing wording and a merchant turning a dish off are doing
 * different jobs, and one long column makes them look like the same job.
 *
 * Every card carries a heading and a line saying what it is for, which is the
 * idiom the shadcn product-create page is the standard example of. The choices
 * card gets the most room, because groups are the structure — how many may be
 * picked, which change the price, which are required — and that is what a menu
 * item has that a product does not.
 *
 * It renders from the `ItemFormController`, the same object the A/B compared
 * against the editor this replaced, so no field here can mean something that
 * editor did not.
 *
 * Two departures from the agreed mockup, both deliberate:
 *   - the price card has no Single price / Sizes segmented control. Choosing
 *     "Sizes" would have to rewrite a group, and a control that reports a change
 *     the model cannot make is one that snaps back — the exact fault the old
 *     editor's group control was rebuilt to remove;
 *   - a Kitchen card carries prep time and the smallest order, which the mockup
 *     did not place. They are part of the old workflow, so leaving them out
 *     would have been the redesign dropping them rather than moving them.
 */

import { useState, type ReactNode } from 'react';
import { View } from 'react-native';
import { formatDayTime, formatMoney, parseMoneyInput, toMoneyInput, type MessageKey } from '@yummy/shared';
import {
  ActionButton,
  Button,
  Callout,
  DateTimeField,
  Divider,
  ErrorBanner,
  ImagePicker,
  PageHeading,
  Price,
  Row,
  Screen,
  SegmentedControl,
  Stack,
  SwitchField,
  Text,
  TextField,
  TimeField,
  ToneBadge,
  Touchable,
  cn,
  useLocale,
  useResponsive,
} from '@yummy/ui-rnr';

import { FooterBar } from '../../components/FooterBar';
import { groupKind, type GroupKind } from '../../lib/item-form';
import type { ItemFormController, OptionGroupRow } from '../../lib/useItemForm';

/**
 * Renders the item editor.
 *
 * @param props The controller the form renders from.
 * @returns The item form.
 */
export function ItemFormLayout({ form }: { form: ItemFormController }) {
  const { t } = useLocale();
  const { width, isTablet } = useResponsive();

  /**
   * Two columns need this much room.
   *
   * The kit's tablet breakpoint (768) is about touch targets, not about fitting
   * a 300-point sidebar beside a form; at 1023 the two columns are still too
   * cramped to read, so every card stays in one column until there is room for
   * both. Below this width the selling cards move up, to just after Choices.
   */
  const isWide = width >= 1024;

  /**
   * Which group's fields are open. One at a time: the summary is what a merchant
   * scans, and several open editors turn the card back into the long form this
   * layout exists to avoid. A new group opens by itself.
   */
  const [editingGroup, setEditingGroup] = useState<number | null>(null);

  const categoryName =
    form.categories.find((category) => category.id === form.categoryId)?.name ??
    t('merchant.menu.uncategorised');

  /**
   * How the dish is sold: the sidebar when there are two columns, and the block
   * that sits after Choices when they stack. Built once and placed by the layout
   * below, so the two arrangements cannot drift apart.
   */
  const sideCards = (
    <>
      {/* No heading: the two switches say what they are, and a title over them
          only repeated the labels. */}
      <FormCard>
        <SwitchField
          label={t('merchant.menu.onMenuLabel')}
          description={t('merchant.menu.onMenuHint')}
          value={form.isActive}
          onValueChange={form.setIsActive}
        />
        <Divider />
        <SwitchField
          label={t('merchant.menu.trackLeftLabel')}
          description={t('merchant.menu.trackLeftHint')}
          value={form.trackInventory}
          onValueChange={form.setTrackInventory}
        />
        {form.trackInventory ? (
          <TextField
            label={t('merchant.menu.inStockLabel')}
            value={form.inventoryCount}
            onChangeText={form.setInventoryCount}
            error={form.errors.inventory}
            keyboardType="number-pad"
          />
        ) : null}
      </FormCard>

      <FormCard title={t('merchant.menu.categoryLabel')} hint={t('merchant.menu.cardCategoryHint')}>
        <Row gap="sm" wrap>
          {form.categories.map((category) => (
            <ChipButton
              key={category.id}
              label={category.name}
              selected={form.categoryId === category.id}
              onPress={() => form.setCategoryId(category.id)}
            />
          ))}
          <ChipButton
            label={t('merchant.menu.uncategorised')}
            selected={form.categoryId === null}
            onPress={() => form.setCategoryId(null)}
          />
        </Row>
      </FormCard>

      <FormCard title={t('merchant.menu.cardPhoto')} hint={t('merchant.menu.cardPhotoHint')}>
        <ImagePicker
          currentUrl={form.photoUrl}
          onPick={form.pickPhoto}
          disabled={form.saving}
          label={form.item?.photo_url ? t('merchant.menu.replacePhoto') : t('merchant.menu.addPhoto')}
        />
        {form.item?.photo_url && !form.pendingPhoto ? (
          <Button
            size="sm"
            variant="ghost"
            onPress={form.toggleRemovePhoto}
            accessibilityLabel={
              form.removePhoto ? t('merchant.menu.keepPhoto') : t('merchant.menu.removePhoto')
            }>
            <Text>
              {form.removePhoto ? t('merchant.menu.keepPhoto') : t('merchant.menu.removePhoto')}
            </Text>
          </Button>
        ) : null}
      </FormCard>
    </>
  );

  return (
    <Screen
      footer={
        <FooterBar>
          <Row gap="sm" justify="end">
            <Button
              variant="secondary"
              onPress={form.onDone}
              disabled={form.saving}
              accessibilityLabel={t('common.cancel')}>
              <Text>{t('common.cancel')}</Text>
            </Button>
            <ActionButton
              label={form.item ? t('common.saveChanges') : t('merchant.menu.addItemToCategory')}
              busy={form.saving}
              onPress={() => void form.save()}
            />
          </Row>
        </FooterBar>
      }>
      {/* The dish's name and its category head the scroll. Every other screen in
          the app lets its heading scroll away, so this one does too rather than
          being the only pinned header left; the name is read once, on the way in,
          and after that the height belongs to the form. */}
      <PageHeading title={form.title} subtitle={categoryName} wrap={false} />

      {form.actionError ? <ErrorBanner message={form.actionError} /> : null}

      <View className={cn('items-start gap-4', isWide ? 'flex-row' : 'flex-col')}>
        {/* What the dish is — and, when the columns stack, how it is sold
            immediately after the choices rather than below everything. */}
        <Stack gap="md" className="w-full min-w-0 flex-1">
          <FormCard title={t('merchant.menu.tabDetails')} hint={t('merchant.menu.cardDetailsHint')}>
            <TextField
              label={t('merchant.menu.nameLabel')}
              required
              value={form.name}
              onChangeText={form.setName}
              error={form.errors.name}
              placeholder={t('merchant.menu.namePlaceholder')}
            />
            <TextField
              label={t('common.description')}
              value={form.description}
              onChangeText={form.setDescription}
              multiline
              numberOfLines={3}
              placeholder={t('merchant.menu.descriptionPlaceholder')}
            />
          </FormCard>

          <FormCard title={t('merchant.menu.cardPrice')} hint={t('merchant.menu.cardPriceHint')}>
            <TextField
              label={t('merchant.menu.priceLabel', { currency: form.currency })}
              required={!form.pricedByGroup}
              disabled={form.pricedByGroup}
              value={form.pricedByGroup ? toMoneyInput(form.cheapestGroupPrice) : form.price}
              onChangeText={form.setPrice}
              error={form.errors.price}
              keyboardType="decimal-pad"
              placeholder={t('merchant.menu.pricePlaceholder')}
              hint={
                form.pricedByGroup
                  ? t('merchant.menu.priceFromGroup')
                  : form.effectivePriceCents !== null
                    ? t('merchant.menu.priceHint', {
                        price: formatMoney(form.effectivePriceCents, form.currency),
                      })
                    : undefined
              }
            />
          </FormCard>

          <FormCard title={t('merchant.menu.cardKitchen')} hint={t('merchant.menu.cardKitchenHint')}>
            <View className={cn('gap-4', isTablet ? 'flex-row' : 'flex-col')}>
              <View className="flex-1">
                <TextField
                  label={t('merchant.menu.prepLabel')}
                  value={form.prepMinutes}
                  onChangeText={form.setPrepMinutes}
                  keyboardType="number-pad"
                  hint={t('merchant.menu.prepHint', { minutes: form.defaultPrepMinutes })}
                />
              </View>
              <View className="flex-1">
                <TextField
                  label={t('merchant.menu.minQuantity')}
                  value={form.minQuantity}
                  onChangeText={form.setMinQuantity}
                  keyboardType="number-pad"
                  hint={t('merchant.menu.minQuantityHint')}
                />
              </View>
            </View>
          </FormCard>

          {/* Choices: the part worth the most room. */}
          <FormCard title={t('merchant.menu.cardChoices')} hint={t('merchant.menu.cardChoicesHint')}>
            {form.groups.length === 0 ? (
              <Text className="text-muted-foreground text-sm">{t('merchant.menu.extrasHint')}</Text>
            ) : null}

            <Stack gap="sm">
              {form.groups.map((group, index) => (
                <GroupCard
                  key={`group-${index}`}
                  form={form}
                  group={group}
                  index={index}
                  open={editingGroup === index}
                  onToggle={() => setEditingGroup(editingGroup === index ? null : index)}
                  onRemove={() => {
                    form.removeGroup(index);
                    setEditingGroup(null);
                  }}
                />
              ))}
            </Stack>

            <Button
              variant="secondary"
              onPress={() => {
                setEditingGroup(form.groups.length);
                form.addGroup();
              }}
              accessibilityLabel={t('merchant.menu.extrasAdd')}>
              <Text>+ {t('merchant.menu.extrasAdd')}</Text>
            </Button>
          </FormCard>

          {/* Stacked, the selling cards come here: after what the dish is,
              before when it is served and what it costs on offer. */}
          {!isWide ? sideCards : null}

          <FormCard
            title={t('merchant.menu.cardAvailability')}
            hint={t('merchant.menu.cardAvailabilityHint')}>
            {form.windows.length === 0 ? (
              <View className="border-border flex-col items-center gap-2 rounded-lg border border-dashed p-4">
                <Text className="font-semibold">{t('merchant.menu.alwaysAvailable')}</Text>
                <Text className="text-muted-foreground text-center text-sm">
                  {t('merchant.menu.alwaysAvailableHint')}
                </Text>
                <Button
                  variant="secondary"
                  onPress={form.addWindow}
                  accessibilityLabel={t('merchant.menu.addServingTime')}>
                  <Text>{t('merchant.menu.addServingTime')}</Text>
                </Button>
              </View>
            ) : (
              <Stack gap="sm">
                {form.windows.map((window, index) => (
                  <View key={`${window.weekday}-${index}`} className="border-border rounded-lg border p-3">
                    <Row spread gap="md" align="center">
                      <Text className="font-semibold">
                        {t(`merchant.hours.weekdayLong${window.weekday}` as MessageKey)}
                      </Text>
                      <Button
                        size="sm"
                        variant="ghost"
                        onPress={() => form.removeWindow(index)}
                        accessibilityLabel={t('merchant.menu.removeWindow')}>
                        <Text>{t('merchant.menu.removeWindow')}</Text>
                      </Button>
                    </Row>
                    <Row gap="md" align="start" className="mt-2">
                      <View className="flex-1">
                        <TimeField
                          label={t('merchant.menu.from')}
                          value={window.start_time}
                          onChange={(value) => form.updateWindow(index, { start_time: value })}
                        />
                      </View>
                      <View className="flex-1">
                        <TimeField
                          label={t('merchant.menu.until')}
                          value={window.end_time}
                          onChange={(value) => form.updateWindow(index, { end_time: value })}
                          // An end of `00:00` reaches the end of the day in the
                          // availability test too, so it reads `24:00` here for
                          // the same reason it does on the hours page.
                          midnightMeansEndOfDay
                        />
                      </View>
                    </Row>
                  </View>
                ))}
              </Stack>
            )}

            <Row gap="sm" wrap className="mt-1">
              <ChipButton
                label={t('merchant.menu.addAnotherDay')}
                onPress={form.addWindow}
                subtle
              />
              {form.windows.length > 0 ? (
                <>
                  <ChipButton label={t('merchant.menu.copyToEveryDay')} onPress={form.copyToEveryDay} subtle />
                  <ChipButton label={t('merchant.menu.clearAll')} onPress={form.clearAllWindows} subtle />
                </>
              ) : null}
            </Row>

            <Text className="text-subtle-foreground mt-2 text-xs">
              {t('merchant.menu.timesNote', { timezone: form.timezone })}
            </Text>
          </FormCard>

          <FormCard title={t('merchant.menu.cardOffer')} hint={t('merchant.menu.cardOfferHint')}>
            {!form.item ? (
              <Callout
                tone="info"
                title={t('merchant.menu.saveFirst')}
                message={t('merchant.menu.saveFirstHint')}
              />
            ) : (
              <>
                {form.item.discounts.length === 0 ? (
                  <Text className="text-muted-foreground text-sm">{t('merchant.menu.noOffers')}</Text>
                ) : (
                  <Stack gap="sm">
                    {form.item.discounts.map((discount) => (
                      <View key={discount.id} className="border-border rounded-lg border p-3">
                        <Row spread gap="md" align="center">
                          <Stack gap="xs" className="min-w-0 flex-1">
                            <Row gap="sm" align="center" wrap>
                              <Text className="font-bold">
                                {discount.kind === 'percent'
                                  ? t('merchant.menu.offPercent', { value: discount.value })
                                  : t('merchant.menu.offAmount', {
                                      value: formatMoney(discount.value, form.currency),
                                    })}
                              </Text>
                              {discount.is_running_now ? (
                                <ToneBadge
                                  label={t('merchant.menu.runningNow')}
                                  tone="success"
                                  emphasis="solid"
                                  size="sm"
                                />
                              ) : Date.parse(discount.ends_at) < Date.now() ? (
                                <ToneBadge label={t('merchant.menu.finished')} tone="neutral" size="sm" />
                              ) : (
                                <ToneBadge
                                  label={t('merchant.menu.scheduledLabel')}
                                  tone="info"
                                  size="sm"
                                />
                              )}
                            </Row>
                            {discount.label ? (
                              <Text className="text-muted-foreground text-sm">{discount.label}</Text>
                            ) : null}
                            <Text className="text-subtle-foreground text-xs">
                              {formatDayTime(discount.starts_at, form.timezone, undefined, t)} →{' '}
                              {formatDayTime(discount.ends_at, form.timezone, undefined, t)}
                            </Text>
                          </Stack>
                          <Button
                            size="sm"
                            variant="ghost"
                            onPress={() => void form.removeDiscount(discount.id)}
                            accessibilityLabel={t('common.remove')}>
                            <Text>{t('common.remove')}</Text>
                          </Button>
                        </Row>
                      </View>
                    ))}
                  </Stack>
                )}

                <Divider />

                <Stack gap="sm">
                  <Text className="text-muted-foreground text-sm font-semibold">
                    {t('merchant.menu.offerType')}
                  </Text>
                  <SegmentedControl
                    value={form.discountKind}
                    onChange={form.setDiscountKind}
                    options={[
                      { value: 'percent', label: t('merchant.menu.percentage') },
                      { value: 'amount', label: t('merchant.menu.amountOff', { currency: form.currency }) },
                    ]}
                  />
                </Stack>

                <View className={cn('gap-4', isTablet ? 'flex-row' : 'flex-col')}>
                  <View className="flex-1">
                    <TextField
                      label={
                        form.discountKind === 'percent'
                          ? t('merchant.menu.percentOff')
                          : t('merchant.menu.amountOffLabel', { currency: form.currency })
                      }
                      value={form.discountValue}
                      onChangeText={form.setDiscountValue}
                      error={form.errors.discount}
                      keyboardType="decimal-pad"
                    />
                  </View>
                  <View className="flex-[2]">
                    <TextField
                      label={t('merchant.menu.offerLabel')}
                      value={form.discountLabel}
                      onChangeText={form.setDiscountLabel}
                      placeholder={t('merchant.menu.offerLabelPlaceholder')}
                      hint={t('merchant.menu.offerLabelHint')}
                    />
                  </View>
                </View>

                <View className={cn('gap-4', isTablet ? 'flex-row' : 'flex-col')}>
                  <View className="flex-1">
                    <DateTimeField
                      label={t('merchant.menu.starts')}
                      value={form.discountStart}
                      onChange={form.setDiscountStart}
                      hint={t('merchant.menu.restaurantDateTime', { timezone: form.timezone })}
                    />
                  </View>
                  <View className="flex-1">
                    <DateTimeField
                      label={t('merchant.menu.ends')}
                      value={form.discountEnd}
                      onChange={form.setDiscountEnd}
                    />
                  </View>
                </View>

                {/* The best idea on the old Offers screen: four fields become
                    one consequence, and it moves as they do.
                    The figure is the kit's `Price`, so an offer reads the same
                    way a markdown does anywhere else — the new price in accent
                    with the old one struck through beside it. */}
                {form.offerPriceCents !== null ? (
                  <Row spread gap="md" align="center" className="border-border mt-1 border-t pt-3">
                    <Text className="text-base font-semibold">
                      {t('merchant.menu.customerPays')}
                    </Text>
                    <Price
                      variant="subheading"
                      cents={form.offerPriceCents}
                      currency={form.currency}
                      compareAtCents={form.effectivePriceCents}
                    />
                  </Row>
                ) : null}

                <ActionButton
                  label={t('merchant.menu.scheduleOfferButton')}
                  busy={form.saving}
                  onPress={() => void form.addDiscount()}
                />
              </>
            )}
          </FormCard>
        </Stack>

        {/* How it is sold, beside the dish when there is room for two columns.
            When the columns stack, the same cards sit after Choices instead. */}
        {isWide ? (
          <Stack gap="md" className="w-[300px] shrink-0">
            {sideCards}
          </Stack>
        ) : null}
      </View>
    </Screen>
  );
}

/**
 * A page card: a heading, a line saying what the card is for, and its content.
 *
 * The heading is optional for the one card that does not need it: the Options
 * card is two switches that already say what they are, and a title over them
 * only restated the labels the merchant was reading.
 *
 * @param props Title, hint, and children.
 * @returns The card.
 */
function FormCard({
  title,
  hint,
  children,
}: {
  title?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <View className="bg-card border-border w-full gap-3 rounded-xl border p-4">
      {title ? (
        <View className="flex-col gap-1">
          <Text className="text-base font-semibold">{title}</Text>
          {hint ? <Text className="text-muted-foreground text-xs">{hint}</Text> : null}
        </View>
      ) : null}
      {children}
    </View>
  );
}

/**
 * One choice group, summarised, and opened in place to edit.
 *
 * The summary is the three things that decide how the customer's menu renders:
 * how many may be picked, whether it is required, and what it does to the price.
 * A merchant can read a group without opening it, which is the whole reason the
 * card can stay collapsed.
 *
 * @param props The controller, the group, and its open/remove handlers.
 * @returns The group card.
 */
function GroupCard({
  form,
  group,
  index,
  open,
  onToggle,
  onRemove,
}: {
  form: ItemFormController;
  group: OptionGroupRow;
  index: number;
  open: boolean;
  onToggle: () => void;
  onRemove: () => void;
}) {
  const { t } = useLocale();
  const kind = groupKind(group);

  const picks =
    group.priceMode === 'absolute' || group.oneOnly
      ? t('merchant.menu.groupPickOne')
      : group.maxSelect.trim() !== ''
        ? t('merchant.menu.groupPickUpTo', { count: group.maxSelect })
        : t('merchant.menu.groupPickAny');
  const required =
    group.priceMode === 'absolute' || group.required
      ? t('merchant.menu.groupRequired')
      : t('merchant.menu.groupOptional');
  const kindLabel =
    kind === 'sets'
      ? t('merchant.menu.groupKindSets')
      : kind === 'free'
        ? t('merchant.menu.groupKindFree')
        : t('merchant.menu.groupKindAdds');
  const shown =
    kind === 'sets'
      ? t('merchant.menu.groupShownRow')
      : kind === 'free'
        ? t('merchant.menu.groupShownChips')
        : t('merchant.menu.groupShownAdds');

  const named = group.choices.filter((choice) => choice.name.trim() !== '');

  return (
    <View
      className={cn(
        'border-border flex-col gap-3 rounded-lg border p-3',
        // An absolute group is a set of prices rather than a set of extras, and
        // it is the one that changes what the dish is.
        kind === 'sets' && 'border-l-primary border-l-[3px]',
      )}>
      <Row spread gap="md" align="start">
        <Stack gap="xs" className="min-w-0 flex-1">
          <Text className="font-semibold">
            {group.name.trim() !== '' ? group.name : t('merchant.menu.extrasNamePlaceholder')}
          </Text>
          <Text className="text-muted-foreground text-xs">
            {picks} · {required} · {kindLabel}
          </Text>
        </Stack>
        <Row gap="xs">
          <Button
            size="sm"
            variant="ghost"
            onPress={onToggle}
            accessibilityLabel={t('merchant.menu.groupEdit')}>
            <Text>{open ? t('merchant.menu.groupDone') : t('merchant.menu.groupEdit')}</Text>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onPress={onRemove}
            accessibilityLabel={t('merchant.menu.extrasRemove')}>
            <Text>{t('merchant.menu.extrasRemove')}</Text>
          </Button>
        </Row>
      </Row>

      {named.length > 0 ? (
        <Row gap="xs" wrap>
          {named.map((choice, choiceIndex) => (
            <ChoiceTag
              key={`${choice.name}-${choiceIndex}`}
              name={choice.name}
              price={choice.price}
              kind={kind}
              currency={form.currency}
            />
          ))}
        </Row>
      ) : null}

      <Text className="text-subtle-foreground text-xs">{shown}</Text>

      {form.groupNeedsName(group) ? (
        <Callout
          tone="warning"
          title={t('merchant.menu.extrasNameRequiredTitle')}
          message={t('merchant.menu.extrasNameRequired')}
        />
      ) : null}
      {form.groupCapTooHigh(group) ? (
        <Callout
          tone="warning"
          title={t('merchant.menu.extrasUpToTooManyTitle')}
          message={t('merchant.menu.extrasUpToTooMany', { count: named.length })}
        />
      ) : null}
      {form.groupDuplicateName(group) ? (
        <Callout
          tone="warning"
          title={t('merchant.menu.extrasDuplicateTitle')}
          message={t('merchant.menu.extrasDuplicate', {
            name: form.groupDuplicateName(group) ?? '',
          })}
        />
      ) : null}
      {form.groupIsFree(group) ? (
        <Callout
          tone="warning"
          title={t('merchant.menu.extrasAllFreeTitle')}
          message={t('merchant.menu.extrasAllFree')}
        />
      ) : null}

      {open ? (
        <Stack gap="md" className="border-border border-t pt-3">
          <View className="flex-col gap-4">
            <TextField
              label={t('merchant.menu.extrasName')}
              value={group.name}
              onChangeText={(name) => form.updateGroup(index, { name })}
              placeholder={t('merchant.menu.extrasNamePlaceholder')}
            />
            <Stack gap="xs">
              <Text className="text-sm font-medium">{t('merchant.menu.extrasPriceQuestion')}</Text>
              <SegmentedControl
                value={form.groupIntent(group)}
                onChange={(intent) =>
                  form.updateGroup(index, {
                    priceMode: intent === 'sets' ? 'absolute' : 'delta',
                  })
                }
                options={[
                  { value: 'extras', label: t('merchant.menu.extrasIntentExtras') },
                  { value: 'sets', label: t('merchant.menu.extrasIntentSets') },
                ]}
              />
              {group.priceMode === 'absolute' ? (
                <Text className="text-muted-foreground text-sm">
                  {t('merchant.menu.extrasSetsPriceConsequence', {
                    price: formatMoney(form.cheapestOfGroup(group), form.currency),
                  })}
                </Text>
              ) : null}
            </Stack>
          </View>

          <Stack gap="xs">
            <Text className="text-sm font-medium">{t('merchant.menu.extrasHowMany')}</Text>
            <SegmentedControl
              value={group.priceMode === 'absolute' || group.oneOnly ? 'one' : 'any'}
              disabled={group.priceMode === 'absolute'}
              onChange={(howMany) =>
                form.updateGroup(index, {
                  oneOnly: howMany === 'one',
                  ...(howMany === 'any' ? { maxSelect: '' } : {}),
                })
              }
              options={[
                { value: 'one', label: t('merchant.menu.extrasHowManyOne') },
                { value: 'any', label: t('merchant.menu.extrasHowManyAny') },
              ]}
            />
            {group.priceMode === 'absolute' ? (
              <Text className="text-muted-foreground text-sm">
                {t('merchant.menu.extrasAbsoluteOneOnly')}
              </Text>
            ) : null}
            {!group.oneOnly && group.priceMode !== 'absolute' ? (
              <TextField
                label={t('merchant.menu.extrasUpTo')}
                value={group.maxSelect || String(Math.max(1, named.length))}
                onChangeText={(maxSelect) => form.updateGroup(index, { maxSelect })}
                keyboardType="number-pad"
                hint={t('merchant.menu.extrasUpToHint', { count: named.length })}
              />
            ) : null}
          </Stack>

          <SwitchField
            label={t('merchant.menu.extrasRequired')}
            value={group.required || group.priceMode === 'absolute'}
            onValueChange={(required) => form.updateGroup(index, { required })}
            disabled={group.priceMode === 'absolute'}
          />

          {group.choices.map((choice, choiceIndex) => (
            <Row key={`choice-${choiceIndex}`} gap="sm" align="end">
              <View className="flex-1">
                <TextField
                  label={t('merchant.menu.extrasChoiceName')}
                  value={choice.name}
                  onChangeText={(name) => form.updateChoice(index, choiceIndex, { name })}
                />
              </View>
              <View className="flex-1">
                <TextField
                  label={t('merchant.menu.extrasChoicePrice', { currency: form.currency })}
                  value={choice.price}
                  onChangeText={(price) => form.updateChoice(index, choiceIndex, { price })}
                  keyboardType="decimal-pad"
                />
              </View>
              <Button
                variant="ghost"
                onPress={() => form.removeChoice(index, choiceIndex)}
                accessibilityLabel={t('merchant.menu.extrasRemoveChoice')}>
                <Text>{t('merchant.menu.remove')}</Text>
              </Button>
            </Row>
          ))}

          <Row>
            <Button
              variant="secondary"
              onPress={() => form.addChoice(index)}
              accessibilityLabel={t('merchant.menu.extrasAddChoice')}>
              <Text>{t('merchant.menu.extrasAddChoice')}</Text>
            </Button>
          </Row>
        </Stack>
      ) : null}
    </View>
  );
}

/**
 * One choice, rendered the way its group renders on the customer menu.
 *
 * The three kinds must be visible, because they are what the customer sees: a
 * free choice is a name, an absolute one carries the price it sets, and a
 * surcharge carries the amount it adds.
 *
 * @param props The choice, its group's kind, and the currency.
 * @returns The tag.
 */
function ChoiceTag({
  name,
  price,
  kind,
  currency,
}: {
  name: string;
  price: string;
  kind: GroupKind;
  currency: string;
}) {
  const cents = parseMoneyInput(price) ?? 0;
  const isSurcharge = kind === 'adds' && cents > 0;
  const label =
    kind === 'sets'
      ? `${name} · ${formatMoney(cents, currency)}`
      : isSurcharge
        ? `${name} +${toMoneyInput(cents)}`
        : name;

  return (
    <View
      className={cn(
        'border-border rounded-full border px-3.5 py-1.5',
        isSurcharge && 'border-primary',
      )}>
      <Text className={cn('text-sm', isSurcharge ? 'text-primary-strong' : 'text-foreground')}>
        {label}
      </Text>
    </View>
  );
}

/**
 * A pressable chip. Used for the small, reversible choices — a category, a bulk
 * availability action — where a full button would overstate them.
 *
 * @param props The label, whether it is chosen, and the press handler.
 * @returns The chip.
 */
function ChipButton({
  label,
  selected,
  subtle,
  onPress,
}: {
  label: string;
  selected?: boolean;
  subtle?: boolean;
  onPress: () => void;
}) {
  return (
    <Touchable
      onPress={onPress}
      accessibilityLabel={label}
      className={cn(
        'rounded-full border px-3.5 py-1.5',
        selected
          ? 'border-primary bg-primary'
          : subtle
            ? 'bg-muted border-transparent'
            : 'border-border bg-card',
      )}>
      <Text
        className={cn(
          'text-sm font-medium',
          selected ? 'text-primary-foreground' : 'text-foreground',
        )}>
        {label}
      </Text>
    </Touchable>
  );
}
